import { Sparkles, TriangleAlert } from "lucide-react";

// ══ product_score_v2 — shared between the product detail (read-only) and
// product edit (read-only preview) pages, so both render one identical
// card instead of two copies that can drift apart. ═══════════════════════

// The API keys the v2 score by scoring stage. I1–I3 all share the 0–24
// month window, so the stage code — not the age range — is the tile's
// identity, and the range is shown as a description under it. Stages the
// backend adds later still render, just without a range, rather than
// being silently dropped.
export const SCORE_STAGE_RANGES = {
  I1: "0–24 Months",
  I2: "0–24 Months",
  I3: "0–24 Months",
  C1: "2–3 Years",
  C2: "4–6 Years",
  C3: "7–9 Years",
  C4: "10–12 Years",
  M1: "Pregnancy",
};

// L1 is not shown as a tile at all.
const HIDDEN_STAGES = new Set(["L1", /* commented: hide 0-24 months stages */ "I1", "I2", "I3"]);

const SCORE_STAGE_ORDER = [/* "I1", "I2", "I3", */ "C1", "C2", "C3", "C4"];

// Known stages keep the order above; anything unrecognised sorts after
// them, alphabetically.
const stageRank = (key) => {
  const i = SCORE_STAGE_ORDER.indexOf(key);
  return i === -1 ? SCORE_STAGE_ORDER.length : i;
};

// Pillar codes (M / N / S) are shown verbatim — the API does not say what
// they stand for, so labelling them would be a guess. They sum to `score`.
const PILLAR_COLORS = { M: "#0C6170", N: "#37BEB0", S: "#A4E5E0" };
const pillarColor = (k) => PILLAR_COLORS[k] || "#c7e9e5";

export const humanizeFlag = (f) => String(f).replace(/_/g, " ");

// Some backend responses carry an internal disclaimer flag (e.g. "TESTING
// ONLY — Not For Public/Production Use") meant for API consumers, not end
// users — strip it out before it ever reaches a rendered flag chip.
const isInternalDisclaimerFlag = (f) => /test(ing)?\s*only|not[\s_-]*for[\s_-]*(public|production)/i.test(String(f));

// ── Score → visual tone mapping (shared by tiles and meters) ───────────
export const scoreTone = (n) => {
  const v = Number(n);
  if (n === null || n === undefined || n === "" || Number.isNaN(v)) {
    return { color: "#9ca3af", track: "#f1f3f4", label: "Not scored" };
  }
  if (v >= 80) return { color: "#0C6170", track: "#e0f7f4", label: "Excellent" };
  if (v >= 60) return { color: "#37BEB0", track: "#eafaf8", label: "Good" };
  if (v >= 40) return { color: "#b45309", track: "#fffbeb", label: "Fair" };
  return { color: "#d94f4f", track: "#fef2f2", label: "Needs attention" };
};

/**
 * Normalizes a raw `product_score_v2` payload (object keyed by stage code,
 * each value either a full `{ score, band, outcome, flags, pillars }`
 * object or a bare number) into a sorted array of stage rows, plus the
 * set of flags shared by every stage (so the card can show them once
 * instead of repeating them per tile).
 *
 * @param {object|null} detail - a loaded review-detail payload
 * @returns {{ stageScores: Array<object>, sharedFlags: string[] }}
 */
export function parseStageScores(detail) {
  const scoreV2 = detail?.product_score_v2 ?? detail?.submission?.product_score_v2 ?? null;

  const stageScores =
    scoreV2 && typeof scoreV2 === "object" && !Array.isArray(scoreV2)
      ? Object.entries(scoreV2)
          .filter(([rawKey]) => !HIDDEN_STAGES.has(String(rawKey).toUpperCase()))
          .map(([rawKey, raw]) => {
            // Tolerate both the full object and a bare numeric score.
            const entry = raw && typeof raw === "object" ? raw : { score: raw };
            const key = String(rawKey).toUpperCase();
            return {
              key,
              range: SCORE_STAGE_RANGES[key] || null,
              score: entry.score ?? null,
              band: entry.band || null,
              outcome: entry.outcome || null,
              flags: Array.isArray(entry.flags) ? entry.flags.filter((f) => !isInternalDisclaimerFlag(f)) : [],
              pillars:
                entry.pillars && typeof entry.pillars === "object"
                  ? Object.entries(entry.pillars).filter(([, v]) => Number(v) > 0)
                  : [],
            };
          })
          .sort((a, b) => stageRank(a.key) - stageRank(b.key) || a.key.localeCompare(b.key))
      : [];

  const flagKey = (st) => [...st.flags].sort().join("|");
  const sharedFlags =
    stageScores.length > 0 && stageScores.every((st) => flagKey(st) === flagKey(stageScores[0]))
      ? stageScores[0].flags
      : [];

  return { stageScores, sharedFlags };
}

/**
 * Read-only per-stage health score card. `product_score_v2` is a computed
 * value (band, pillar breakdown, allergen flags) — not something a
 * reviewer types in — so this renders identically wherever it appears
 * rather than exposing editable inputs.
 */
export default function StageScoreCard({ stageScores, sharedFlags = [], targetStage = null, title = "Health Scores by Stage", subtitle = "Nutritional & safety rating per scoring stage" }) {
  if (!stageScores || stageScores.length === 0) return null;

  return (
    <div className="pw-card">
      <div className="pw-sec-h">
        <div className="pw-sec-icn"><Sparkles size={15} /></div>
        <div className="flex-1 min-w-0">
          <div className="pw-sec-t">{title}</div>
          <div className="pw-sec-sub">{subtitle}</div>
        </div>
        <span className="pw-count">{stageScores.length}</span>
      </div>

      <div className="pw-sec-b">
        {sharedFlags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 14 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#37BEB0", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <TriangleAlert size={11} color="#d94f4f" /> Flags on every stage
            </span>
            <div className="pw-flags">
              {sharedFlags.map((f) => (
                <span key={f} className="pw-flag-chip">{humanizeFlag(f)}</span>
              ))}
            </div>
          </div>
        )}

        <div className="pw-ages">
          {stageScores.map((st) => {
            const isTarget = targetStage === st.key;
            const hasScore = st.score !== null && st.score !== undefined && st.score !== "";
            const numScore = hasScore ? Number(st.score) : null;
            const tone = scoreTone(hasScore ? numScore : null);
            const showFlags = sharedFlags.length === 0 && st.flags.length > 0;

            return (
              <div key={st.key} className={`pw-age${isTarget ? " pw-age-t" : ""}`}>
                <div className="flex items-center justify-between gap-1.5">
                  {/* Age range is the heading; the stage code is only a
                      fallback for stages with no range mapped. */}
                  <span className="pw-age-lbl">{st.range || st.key}</span>
                  {isTarget && <span className="pw-tgt">Target</span>}
                  {st.band && (
                    <span className="pw-band" style={{ color: tone.color, marginLeft: "auto" }}>
                      Band {st.band}
                    </span>
                  )}
                </div>

                <div className="flex items-baseline gap-1">
                  {hasScore ? (
                    <>
                      <span className="pw-age-val" style={{ color: tone.color }}>
                        {Number.isInteger(numScore) ? numScore : numScore.toFixed(1)}
                      </span>
                      <span style={{ fontSize: 10.5, color: "#A4E5E0", fontWeight: 700 }}>/ 100</span>
                    </>
                  ) : (
                    <span className="pw-outcome">
                      {st.outcome ? humanizeFlag(st.outcome) : "Not scored"}
                    </span>
                  )}
                </div>

                {/* Pillars sum to the score, so the bar doubles as a
                    breakdown of how the score was composed. */}
                {st.pillars.length > 0 ? (
                  <div className="pw-meter pw-meter-seg">
                    {st.pillars.map(([k, v]) => (
                      <i
                        key={k}
                        title={`${k}: ${v}`}
                        style={{ width: `${Math.max(0, Math.min(100, Number(v) || 0))}%`, background: pillarColor(k) }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="pw-meter">
                    <i style={{ width: `${hasScore ? Math.max(0, Math.min(100, numScore)) : 0}%`, background: tone.color }} />
                  </div>
                )}

                {st.pillars.length > 0 && (
                  <div className="pw-pillars">
                    {st.pillars.map(([k, v]) => (
                      <span key={k}>
                        <i style={{ background: pillarColor(k) }} />
                        {k} <b>{v}</b>
                      </span>
                    ))}
                  </div>
                )}

                {showFlags && (
                  <div className="pw-flags">
                    {st.flags.map((f) => (
                      <span key={f} className="pw-flag-chip">{humanizeFlag(f)}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
